import tokens from '../design/tokens';
import { fetchAuthSession } from 'aws-amplify/auth';
/**
 * Monitoring API client — talks to the existing byrdsense-monitoring-api Lambda.
 * Also reuses byrdsense-motor-report Lambda for PDF generation.
 */

const MONITORING_API_URL = 'https://gv0ivby9md.execute-api.us-east-1.amazonaws.com/prod/monitoring';
const REPORT_API_URL = 'https://slrai28hb2.execute-api.us-east-1.amazonaws.com/prod/reports';

/**
 * The signed-in user's Cognito ID token as an Authorization header (gate 8).
 * Amplify refreshes it. No session → no header: the API is still open, so a
 * tokenless call behaves exactly as before.
 */
export async function authHeader() {
  try {
    const session = await fetchAuthSession();
    const idToken = session?.tokens?.idToken?.toString();
    return idToken ? { Authorization: idToken } : {};
  } catch {
    return {};
  }
}

async function apiFetch(path, options = {}) {
  const url = `${MONITORING_API_URL}/${path}`;
  const auth = await authHeader();
  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...auth, ...options.headers },
    });
  } catch (err) {
    throw new Error('Network error — check your internet connection');
  }
  let result;
  try {
    result = await response.json();
  } catch {
    throw new Error(`API returned invalid response (HTTP ${response.status})`);
  }
  if (!response.ok) {
    throw new Error(result.error || `API error: ${response.status}`);
  }
  return result;
}

/** List motors for a customer. customerId is required for the portal. */
export async function listMotors(customerId) {
  const qs = customerId ? `motors?customerId=${encodeURIComponent(customerId)}` : 'motors';
  return apiFetch(qs);
}

/** Weekly report PDFs indexed for this customer, newest first. No S3 keys. */
export async function listReports(customerId) {
  return apiFetch(`reports?customerId=${encodeURIComponent(customerId)}`);
}

/** Screening PDFs indexed for this customer, newest first. No S3 keys. */
export async function listScreenings(customerId) {
  return apiFetch(`screenings?customerId=${encodeURIComponent(customerId)}`);
}

/**
 * A 5-minute signed download link for one document, by docId.
 * kind: 'reports' | 'screenings'. Returns { url, expiresIn, filename }.
 */
export async function getDocumentDownload(kind, docId, customerId) {
  return apiFetch(`${kind}/${encodeURIComponent(docId)}/download?customerId=${encodeURIComponent(customerId)}`);
}

/** The customer's Calls & alerts log, newest first. Read-only. */
export async function listCalls(customerId) {
  return apiFetch(`calls?customerId=${encodeURIComponent(customerId)}`);
}

/** Get time-series readings. period: '24h' | '7d' | '30d' */
export async function getMotorReadings(motorId, period = '24h') {
  return apiFetch(`motors/${encodeURIComponent(motorId)}/readings?period=${period}`);
}

/** Get latest AI analysis for a motor */
export async function getMotorAnalysis(motorId) {
  return apiFetch(`motors/${encodeURIComponent(motorId)}/analysis`);
}

/** Generate a PDF health report. Returns { downloadUrl, s3Key, readingCount }. */
export async function generateMotorReport(motorId) {
  const url = `${REPORT_API_URL}/motors/${encodeURIComponent(motorId)}`;
  let response;
  try {
    response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (err) {
    throw new Error('Network error — check your internet connection');
  }
  let result;
  try {
    result = await response.json();
  } catch {
    throw new Error(`Report API returned invalid response (HTTP ${response.status})`);
  }
  if (!response.ok) {
    throw new Error(result.error || `Report API error: ${response.status}`);
  }
  return result;
}

// ─── ISO 20816 (formerly ISO 10816) helpers ─────────────────────────────────

export const ISO_THRESHOLDS = {
  small:  { good: 0.71, satisfactory: 1.8,  unsatisfactory: 4.5  },
  medium: { good: 1.12, satisfactory: 2.8,  unsatisfactory: 7.1  },
  large:  { good: 1.8,  satisfactory: 4.5,  unsatisfactory: 11.2 },
};

// Medium starts above 15 HP — the same cut as the PDF's get_size_class
// (gate 16, Pete 2026-10-06). Until then the portal used > 20 HP, so a 16–20 HP
// motor could grade differently on its card and in its report.
export function getSizeClass(hp) {
  if (!hp) return 'small';
  if (hp > 100) return 'large';
  if (hp > 15) return 'medium';
  return 'small';
}

export function getISOSeverity(velocity, sizeClass = 'small') {
  if (velocity == null) return null;
  const t = ISO_THRESHOLDS[sizeClass] || ISO_THRESHOLDS.small;
  if (velocity > t.unsatisfactory) return 'UNACCEPTABLE';
  if (velocity > t.satisfactory) return 'UNSATISFACTORY';
  if (velocity > t.good) return 'SATISFACTORY';
  return 'GOOD';
}

// Retargeted onto the design tokens (Sep 2026 visual system) so the portal,
// the public site and the PDF all use one set of ISO status colors.
export const SEVERITY_COLORS = {
  GOOD: tokens.color.status.good,             // #1E7A47
  SATISFACTORY: tokens.color.status.satisfactory, // #2F6FC4 — B reads as blue, not a warning
  UNSATISFACTORY: tokens.color.status.watch,  // #9A5B00
  UNACCEPTABLE: tokens.color.status.attention,// #C2410C
  CRITICAL: tokens.color.status.critical,     // #B91C1C
};

// Tinted chip backgrounds + the card's 4px top border stroke, per severity.
export const SEVERITY_BG = {
  GOOD: tokens.color.statusBg.good,
  SATISFACTORY: tokens.color.statusBg.satisfactory,
  UNSATISFACTORY: tokens.color.statusBg.watch,
  UNACCEPTABLE: tokens.color.statusBg.attention,
  CRITICAL: tokens.color.statusBg.critical,
};

export const SEVERITY_STROKE = {
  GOOD: tokens.color.status.goodOnDark,          // #3DD68C
  SATISFACTORY: tokens.color.primary,            // #4A90E2
  UNSATISFACTORY: tokens.color.status.watchStroke,   // #F5A524
  UNACCEPTABLE: tokens.color.status.attentionStroke, // #F0563C
  CRITICAL: tokens.color.status.criticalStroke,      // #EF4444 (= PDF F)
};

export const SEVERITY_LABELS = {
  GOOD: 'Good',
  SATISFACTORY: 'Satisfactory',
  UNSATISFACTORY: 'Watch',
  UNACCEPTABLE: 'Attention',
  CRITICAL: 'Critical',
};

/**
 * Velocity -> letter grade. This is a direct port of _grade_from_velocity in
 * lambda/motor-report/lambda_function.py (the onboarding repo) and MUST stay in
 * step with it: a customer who sees "D" on this card and "C" in their PDF has
 * caught us contradicting ourselves.
 *
 *   A: <= good            B: <= satisfactory
 *   C: <= unsatisfactory  D: <= unsatisfactory * 1.58   F: above that
 */
export function gradeFromVelocity(velocity, hp) {
  // A missing reading is not a grade. Neither is a zero: 0.00 mm/s means the
  // motor was off or the sensor stopped reporting, and grading that "A" told
  // a customer their dead sensor was a healthy motor. The PDF's
  // _grade_from_velocity returns its no-data grade for exactly these inputs
  // (byrdsense-onboarding, 2026-09-22) — a motor must never read A on a card
  // and "No data" in its report. Callers already treat null as "no chip".
  if (velocity == null) return null;
  const v = Number(velocity);
  if (!Number.isFinite(v) || v <= 0) return null;
  const t = ISO_THRESHOLDS[getSizeClass(hp)] || ISO_THRESHOLDS.small;
  if (v <= t.good) return 'A';
  if (v <= t.satisfactory) return 'B';
  if (v <= t.unsatisfactory) return 'C';
  if (v <= t.unsatisfactory * 1.58) return 'D';
  return 'F';
}

const GRADE_RANK = { A: 4, B: 3, C: 2, D: 1, F: 0 };

/**
 * The fleet's worst grade — what the Fleet health ring shows. A plant is only
 * as healthy as its worst motor. Until 2026-09-25 the ring AVERAGED the grades
 * into a GPA letter under the label "Worst reading this week", so one D hid
 * behind the healthy motors: the demo fleet read B+ with a D motor on the list.
 * No-data (null) is not a grade and is ignored; no grades at all -> null.
 */
export function worstGrade(grades) {
  return grades
    .filter((g) => g in GRADE_RANK)
    .reduce((worst, g) => (worst == null || GRADE_RANK[g] < GRADE_RANK[worst] ? g : worst), null);
}

/**
 * Grade -> Fleet count tile. Mirrors the PDF's GRADE_CONFIG labels exactly
 * (lambda/motor-report): A/B Good, C Watch, D Attention, F Critical. Until
 * 2026-09-25 the tiles counted by colour bucket, so every B motor showed as
 * "Watch" on screen while its report said "Good". No-data -> null (no tile).
 */
export function tileForGrade(grade) {
  switch (grade) {
    case 'A':
    case 'B': return 'good';
    case 'C': return 'watch';
    case 'D': return 'attention';
    case 'F': return 'critical';
    default: return null;
  }
}

/**
 * Fleet tiles + ring from each motor's { grade, offline }. A motor that is
 * offline (silent > 2h, see isOffline) or has no gradeable reading counts ONLY
 * as Offline — never Good/Watch/Attention/Critical — matching the PDF's
 * "No data" handling: a silent sensor is not a healthy motor, and its last
 * reading is not today's. The ring's worst grade is over reporting motors only.
 */
export function fleetSummary(rows) {
  const counts = { good: 0, watch: 0, attention: 0, critical: 0, offline: 0 };
  const reporting = [];
  rows.forEach(({ grade, offline }) => {
    const tile = tileForGrade(grade);
    if (offline || !tile) { counts.offline++; return; }
    counts[tile]++;
    reporting.push(grade);
  });
  return { ...counts, worst: worstGrade(reporting) };
}

/**
 * Grade -> the one action sentence on a motor card. Same words as the PDF's
 * _fallback_assessment for that grade. It was keyed on the colour bucket and
 * ran one grade harsh: a B card said "within 30 days", a D card "48 hours".
 */
export function actionForGrade(grade) {
  switch (grade) {
    case 'A':
    case 'B': return 'Normal for this size. Keep it on the monitoring list.';
    case 'C': return 'Re-check within 30 days.';
    case 'D': return 'Plan an inspection within two weeks.';
    case 'F': return 'Inspect within 48 hours.';
    default: return null;
  }
}

/**
 * Trend over the sparkline window (monitoring-api sends one mean velocity per
 * day for the last 60 days). Compares the mean of the LAST 7 daily points with
 * the mean of the FIRST 7: >= +10% rising, <= -10% falling, otherwise steady.
 * Returns null — and the card shows NO trend and NO sparkline — when there is
 * not enough to compare (fewer than 14 daily points, or under 21 days
 * spanned). A made-up "flat" is worse than saying nothing.
 */
export const TREND_MIN_POINTS = 14;
export const TREND_MIN_SPAN_DAYS = 21;
export const TREND_THRESHOLD_PCT = 10;

export function historyValues(history) {
  if (!Array.isArray(history)) return [];
  return history
    .map((p) => (typeof p === 'number' ? p : p?.rmsVelocity))
    .filter((v) => Number.isFinite(v) && v > 0);
}

export function trendFromHistory(history) {
  const vals = historyValues(history);
  if (vals.length < TREND_MIN_POINTS) return null;
  const days = Array.isArray(history) ? history.map((p) => p?.day).filter(Boolean) : [];
  if (days.length >= 2) {
    const span = (new Date(days[days.length - 1]) - new Date(days[0])) / 864e5;
    if (!(span >= TREND_MIN_SPAN_DAYS)) return null;
  }
  const mean = (a) => a.reduce((x, y) => x + y, 0) / a.length;
  const first = mean(vals.slice(0, 7));
  const last = mean(vals.slice(-7));
  if (!(first > 0)) return null;
  const pct = ((last - first) / first) * 100;
  const direction = pct >= TREND_THRESHOLD_PCT ? 'rising' : pct <= -TREND_THRESHOLD_PCT ? 'falling' : 'steady';
  const arrow = { rising: '↑', falling: '↓', steady: '→' }[direction];
  return { direction, pct, label: `${arrow} ${direction}` };
}

/**
 * Sparkline y-range. The span is at least 15% of the motor's own level, so a
 * steady motor's ±1% day-to-day noise draws as a flat line instead of filling
 * the box and looking like a fault.
 */
export function sparklineRange(vals) {
  const min = Math.min(...vals), max = Math.max(...vals);
  const mid = (min + max) / 2;
  const span = Math.max(max - min, mid * 0.15, 1e-6);
  return { lo: mid - span / 2, hi: mid + span / 2 };
}

/** Share of the ring to fill for a grade: A full, F empty. */
export function gradeRingFraction(grade) {
  return grade in GRADE_RANK ? GRADE_RANK[grade] / 4 : 0;
}

/**
 * ISO 20816 zone bands for a motor's size class — the SAME thresholds
 * gradeFromVelocity (and the PDF's _grade_from_velocity) grade against, so a
 * chart can never put a reading in one zone while its grade says another:
 *   Good (A)  <= good  <  Satisfactory (B)  <= satisfactory  <
 *   Unsatisfactory (C)  <= unsatisfactory  <  D  <= unsatisfactory * 1.58  <  F
 * D and F are separate bands (Batch B, 2026-10-06): until then the top band
 * read "D/F > 7.1", merging two grades with different actions.
 * Until 2026-09-25 the MotorDetail chart skipped `satisfactory`, so ISO B and
 * C were one band labelled "SATISFACTORY" (1.12–7.1 on a 60 HP motor).
 */
export function isoBands(hp) {
  const t = ISO_THRESHOLDS[getSizeClass(hp)] || ISO_THRESHOLDS.small;
  const dTop = t.unsatisfactory * 1.58;        // gradeFromVelocity's D ceiling
  const dTopTxt = (+dTop.toFixed(1));          // as the PDF prints it
  return [
    { zone: 'good',           grade: 'A', lo: 0,                hi: t.good,           label: `A ≤ ${t.good}` },
    { zone: 'satisfactory',   grade: 'B', lo: t.good,           hi: t.satisfactory,   label: `B ${t.good}–${t.satisfactory}` },
    { zone: 'unsatisfactory', grade: 'C', lo: t.satisfactory,   hi: t.unsatisfactory, label: `C ${t.satisfactory}–${t.unsatisfactory}` },
    { zone: 'attention',      grade: 'D', lo: t.unsatisfactory, hi: dTop,             label: `D ${t.unsatisfactory}–${dTopTxt}` },
    { zone: 'critical',       grade: 'F', lo: dTop,             hi: Infinity,         label: `F > ${dTopTxt}` },
  ];
}

/** Grade -> the status key used for colour. Mirrors the PDF's GRADE_CONFIG. */
export function severityForGrade(grade) {
  switch (grade) {
    case 'A': return 'GOOD';
    case 'B': return 'SATISFACTORY';
    case 'C': return 'UNSATISFACTORY';
    case 'D': return 'UNACCEPTABLE';
    case 'F': return 'CRITICAL';
    default: return null;
  }
}

export function timeAgo(isoTimestamp) {
  if (!isoTimestamp) return 'Never';
  const diff = Date.now() - new Date(isoTimestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * "Offline · last reading 2d ago" — the one offline line on the Fleet card,
 * the motor detail badge and its Analysis card (Batch D2 #10, 2026-10-08).
 */
export function offlineLabel(isoTimestamp) {
  return isoTimestamp ? `Offline · last reading ${timeAgo(isoTimestamp)}` : 'Offline · no reading yet';
}

/** An offline motor's last grade is drawn at this opacity — kept, not current. */
export const FADED_OPACITY = 0.45;

/**
 * Celsius -> Fahrenheit. THE single conversion in this app.
 *
 * Sensors report Celsius; Node-RED stores it verbatim as `temperatureC`, and
 * byrdsense-monitoring-api passes it through under the shorter name
 * `temperature` — which is where the unit gets lost. Every Byrdsense surface
 * DISPLAYS Fahrenheit, so anything rendering an API temperature must come
 * through here first.
 *
 * This mirrors `c_to_f` in lambda/motor-report/lambda_function.py (the
 * onboarding repo). Change one, change both, or a motor reads one temperature
 * on a card and another in its report.
 *
 * Returns a rounded number, or null — callers render null as an em dash.
 */
export function cToF(celsius) {
  if (celsius == null) return null;
  const c = Number(celsius);
  if (!Number.isFinite(c)) return null;
  return Math.round(c * 9 / 5 + 32);
}

export function isOffline(isoTimestamp) {
  if (!isoTimestamp) return true;
  return Date.now() - new Date(isoTimestamp).getTime() > 2 * 60 * 60 * 1000;
}
